import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaUmbrellaBeach } from "react-icons/fa";
import "../css/styles.css";

const TanzaniaZanzibar = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348758/10_Day_Tanzania_Safari_Zanzibar_hohxsc.jpg"
            alt="Tanzania Safari and Zanzibar Beach"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Tanzania Safari &amp; Beach</span>
          <h1>10 Day Tanzania Safari &amp; Zanzibar</h1>
          <p className="safari-detail-hero-sub">
            From the Serengeti's endless plains and the Ngorongoro Crater to the white sandy beaches of Zanzibar — the best of both worlds.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>Safari Meets Paradise</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This journey combines the thrill of an African safari with the peaceful beauty of the Indian Ocean. Begin your adventure exploring Tanzania's famous wildlife parks, including the elephant-filled landscapes of Tarangire National Park, the endless plains of Serengeti National Park, and the breathtaking Ngorongoro Crater.
          </p>
          <p>
            After days of exciting wildlife encounters, your journey continues to the tropical island of Zanzibar, where white sandy beaches and turquoise waters offer the perfect place to relax. This safari is ideal for travelers who want the best of both worlds — thrilling safari adventures and peaceful beach moments.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore three of Tanzania's most famous wildlife parks</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>See elephants, lions, giraffes, zebras, and many other animals</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Descend into the remarkable Ngorongoro Crater</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Experience the vast plains of Serengeti National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Relax on the white sandy beaches of Zanzibar Island</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>10 Days / 9 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Arusha / Zanzibar</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaUmbrellaBeach className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Tarangire &ndash; Serengeti &ndash; Ngorongoro &ndash; Zanzibar</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>Jun–Oct, Dec–Mar</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Arusha</h3>
              <p>
                Arrive at Kilimanjaro International Airport, where your Nyatwende representative will welcome you to Tanzania. You will then transfer to your hotel in Arusha, a peaceful town surrounded by green hills and coffee farms.
              </p>
              <p>Spend the rest of the day relaxing after your journey.</p>
              <p className="safari-detail-day-note">
                Overnight in Arusha. <br />
                <strong>Accommodation:</strong> Arusha Serena Hotel / Gran Meli&aacute; Arusha
              </p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Arusha to Tarangire National Park</h3>
              <p>
                After breakfast, depart for Tarangire National Park, a beautiful park famous for its giant baobab trees and large elephant herds.
              </p>
              <p>
                Enjoy your first game drive as you explore the park's golden landscapes. The Tarangire River attracts many animals, making it an excellent place to see elephants, zebras, giraffes, and antelopes.
              </p>
              <p className="safari-detail-day-note">Overnight in Tarangire.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Tarangire to Serengeti National Park</h3>
              <p>
                After breakfast, travel toward the legendary Serengeti National Park. The landscape slowly opens into wide savannah plains where wildlife roams freely.
              </p>
              <p>
                Arrive in the Serengeti and begin your first game drive across these famous plains, home to lions, cheetahs, elephants, and large herds of grazing animals.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Full Day in Serengeti</h3>
              <p>
                Spend the day exploring the extraordinary wildlife of the Serengeti. Morning and afternoon game drives allow you to discover the park's diverse landscapes and wildlife. Depending on the season, you may even witness the Great Wildebeest Migration as thousands of animals move across the plains.
              </p>
              <p>
                Optional experience: a sunrise hot air balloon safari, offering a magical view of the Serengeti from the sky.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Serengeti to Ngorongoro</h3>
              <p>
                After breakfast, enjoy a final morning game drive before traveling toward the Ngorongoro Conservation Area.
              </p>
              <p>
                Arrive at your lodge near the famous Ngorongoro Crater and enjoy breathtaking views of this natural wonder.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Ngorongoro Crater Safari</h3>
              <p>
                After an early breakfast, descend into the Ngorongoro Crater for a memorable game drive. The crater is home to an incredible concentration of wildlife including elephants, lions, buffalo, zebras, and sometimes the rare black rhino.
              </p>
              <p>
                Enjoy a picnic lunch surrounded by spectacular scenery. Return to your lodge in the afternoon.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Ngorongoro to Zanzibar</h3>
              <p>
                After breakfast, drive back to Arusha and transfer to the airport for your flight to the island paradise of Zanzibar.
              </p>
              <p>
                Upon arrival, transfer to your beach resort where soft white sands and turquoise waters await.
              </p>
              <p className="safari-detail-day-note">Overnight in Zanzibar.</p>
            </div>
          </div>

          {/* Days 8–9 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">8‑9</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 8–9 — Zanzibar Beach Relaxation</h3>
              <p>
                Spend two full days enjoying the beautiful beaches of Zanzibar. Relax by the ocean, swim in the warm Indian Ocean waters, or explore the island's culture and history.
              </p>
              <p><strong>Optional activities include:</strong></p>
              <ul className="safari-detail-day-list">
                <li>Snorkeling and diving</li>
                <li>Spice farm tours</li>
                <li>Exploring the historic streets of Stone Town</li>
                <li>Sunset dhow cruises</li>
              </ul>
              <p className="safari-detail-day-note">Overnight in Zanzibar.</p>
            </div>
          </div>

          {/* Day 10 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">10</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 10 — Zanzibar Departure</h3>
              <p>
                After breakfast, transfer to Abeid Amani Karume International Airport for your onward flight.
              </p>
              <p className="safari-detail-day-note">
                Your Tanzania safari and beach holiday ends here, leaving you with unforgettable memories of wildlife adventures and peaceful island moments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Options</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Arusha */}
        <h3 className="safari-detail-accom-region-title">Arusha</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">City Hotels</div>
            <ul>
              <li><FaHotel /> Arusha Serena Hotel</li>
              <li><FaHotel /> Gran Meli&aacute; Arusha</li>
            </ul>
          </div>
        </div>

        {/* Tarangire */}
        <h3 className="safari-detail-accom-region-title">Tarangire</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Tarangire Sopa Lodge</li>
              <li><FaHotel /> Tarangire Simba Lodge</li>
            </ul>
          </div>
        </div>

        {/* Serengeti */}
        <h3 className="safari-detail-accom-region-title">Serengeti</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Serengeti Serena Safari Lodge</li>
              <li><FaHotel /> Serengeti Simba Lodge</li>
            </ul>
          </div>
        </div>

        {/* Ngorongoro */}
        <h3 className="safari-detail-accom-region-title">Ngorongoro</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ngorongoro Serena Safari Lodge</li>
              <li><FaHotel /> Ngorongoro Sopa Lodge</li>
            </ul>
          </div>
        </div>

        {/* Zanzibar */}
        <h3 className="safari-detail-accom-region-title">Zanzibar</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Beach Resorts</div>
            <ul>
              <li><FaHotel /> Essque Zalu Zanzibar</li>
              <li><FaHotel /> Zanzibar Serena Hotel</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          All lodges and resorts are carefully selected for comfort, location, and memorable experiences. Similar properties of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Arusha and Zanzibar</li>
              <li>Transport in a safari vehicle with professional driver-guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during safari and beach stay</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
              <li>Domestic flight from Arusha to Zanzibar</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as hot air balloon safari or snorkeling excursions</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can easily be tailored to match your travel style. Extend your stay in Zanzibar, upgrade to luxury safari lodges or beachfront villas, or add unique experiences such as sunset dhow cruises or private beach dinners.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=10 Day Tanzania Safari and Zanzibar" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TanzaniaZanzibar;
